import { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { Container, TabHeader, FormGroupLine, FormGroupColumn, ErrorMessageWrapper } from "./styles";

import Button from "../../components/Button";
import ButtonBack from "../../components/ButtonBack";
import Input from "../../components/Form/Input";
import ErrorMessage from "../../components/Form/ErrorMessage";
import Spacing from "../../components/Spacing";
import Text from "../../components/Text";
import registerSchemaCpf, {
  clearEmailsValidated,
  clearCpfsValidated,
} from "../../utils/schemas/registerCpf";
import registerSchemaCnpj from "../../utils/schemas/registerCnpj";
import { createUser, login, logout } from "../../services/user";
import m from "../../utils/messages";
import ErrorBox from "../../components/ErrorBox";
import { FormGroup, Label } from "../../components/Form";
import Loading from "../../components/Loading";
import RadioGroup from "../../components/Form/RadioGroup";
import PasswordRules from "../../components/PasswordRules";
import SimpleModal from "./modal";
import Swal from "sweetalert2";

const INITIAL_FORM_VALUES = {
  email: "",
  name: "",
  surname: "",
  document: "",
  birthdate: "",
  password: "",
  password_confirmation: "",
  telephone: "",
  gender: "",
  dataUsageAgreement: false,
};

const removeNumbers = (value) => {
  return value?.replace(/[0-9]/g, '') || "";
};

// Função para formatar o documento (CPF ou CNPJ)
const formatDocument = (value) => {
  if (!value) return "";

  // Remove todos os caracteres não-numéricos
  const digits = value.replace(/\D/g, '');

  // Formata como CNPJ
  if (digits.length > 11) {
    return digits
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18); // 18 caracteres é o máximo para CNPJ formatado
  }

  // Formata como CPF
  return digits
    .replace(/(\d{3})(?=\d)/, '$1.')
    .replace(/(\d{3})(?=\d)/, '$1.')
    .replace(/(\d{3})(?=\d)/, '$1-')
    .substring(0, 14); // 14 caracteres é o máximo para CPF formatado
};

// Função para formatar números (remover caracteres não numéricos)
const formatNumbers = (value) => {
  if (!value) return "";

  // Remove todos os caracteres não-numéricos
  return value.replace(/\D/g, '');
};

// Função para validar CPF
const validateCPF = (cpf) => {
  cpf = cpf.replace(/[^\d]+/g, '');

  if (cpf.length !== 11) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) return false;

  // Valida os dígitos verificadores
  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum = sum + parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum = sum + parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;

  return true;
};

// Função para validar CNPJ
const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/[^\d]+/g, '');

  if (cnpj.length !== 14) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cnpj)) return false;

  // Valida os dígitos verificadores
  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
};

const Register = () => {
  const [success, setSuccess] = useState(false);
  const history = useHistory();
  const dataLayer = window?.dataLayer;
  const ref = useRef(null);
  const [cpf, setCpf] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [dataUsageAgreementChecked, setDataUsageAgreementChecked] = useState(false);
  const [documentDigitsCount, setDocumentDigitsCount] = useState(0);
  const [expectedDigits, setExpectedDigits] = useState(11); // Começa com CPF
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [documentType, setDocumentType] = useState("CPF");
  const [documentIsValid, setDocumentIsValid] = useState(false);
  const [invalidFieldsList, setInvalidFieldsList] = useState([]);
  const [documentError, setDocumentError] = useState("");

  const locationState = history.location.state || {};

  const [error, setError] = useState(null);

  const [passwordCriteria, setPasswordCriteria] = useState({
    uppercase: false,
    lowercase: false,
    number: false,
    length: false,
  });

  const handleCheckboxChange = () => {
    setDataUsageAgreementChecked(!dataUsageAgreementChecked);
  };

  const handleSuccess = (email, password) => {
    Swal.fire({
      title: "Cadastro realizado com sucesso",
      text: "Você será redirecionado de volta ao site",
      icon: "success",
      confirmButtonColor: "#CB3D42",
      didClose: async () => {
        try {
          const credentials = {
            email,
            password,
          };

          const data = await login(credentials);

          window.location.href = data.redirectTo;
        } catch (err) {
          setError(m("internalError"));
        }
      },
    });
  };

  const handleError = (error) => {
    console.log('Erro recebido:', error);

    let errorMessage = "Algo deu errado, tente novamente!";
    let errorTitle = "Atenção";

    // Verifica diferentes formatos possíveis de erro
    if (error?.response?.data?.errors) {
      // Verificar erro de CPF/CNPJ
      if (error.response.data.errors.document) {
        const docErrors = error.response.data.errors.document;

        if (docErrors.some(msg => msg.includes("já está sendo utilizado") || msg.includes("já está em uso"))) {
          errorTitle = `${documentType} já cadastrado`;
          errorMessage = `Este ${documentType} já está cadastrado em nossa base de dados. Por favor, tente fazer login ou recuperar sua senha.`;
        } else if (docErrors.some(msg => msg.includes("inválido"))) {
          errorTitle = `${documentType} inválido`;
          errorMessage = `O ${documentType} informado não é válido. Por favor, verifique os dados e tente novamente.`;
        } else {
          errorMessage = docErrors.join('<br>');
        }
      } else {
        errorMessage = Object.values(error.response.data.errors)
          .flat()
          .join('<br>');
      }
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    Swal.fire({
      icon: "error",
      title: errorTitle,
      html: errorMessage,
      confirmButtonColor: "#CB3D42",
      confirmButtonText: "OK"
    });
  };

  const handleDocumentChange = (e) => {
    const inputValue = e.target.value;

    // Remove tudo que não é número
    const digits = inputValue.replace(/\D/g, '');

    // Atualiza a contagem APENAS dos dígitos numéricos
    setDocumentDigitsCount(digits.length);

    // Define se é CPF ou CNPJ baseado no número de dígitos
    if (digits.length > 11) {
      setCpf(false);
      setExpectedDigits(14);
      setDocumentType("CNPJ");
    } else {
      setCpf(true);
      setExpectedDigits(11);
      setDocumentType("CPF");
    }

    // Formata e define o valor
    const formattedValue = formatDocument(digits);
    ref.current.setFieldValue('document', formattedValue);

    // Validar o documento e atualizar o estado de validade
    const isValidDocument = digits.length === 11 ?
      validateCPF(digits) :
      (digits.length === 14 ? validateCNPJ(digits) : false);

    setDocumentIsValid(isValidDocument);

    if (digits.length === 0) {
      setDocumentError("");
      setShowAdditionalFields(false);
    } else if (digits.length !== 11 && digits.length !== 14) {
      setDocumentError(`${documentType} incompleto. Por favor, insira todos os dígitos.`);
      setShowAdditionalFields(false);
    } else if (!isValidDocument) {
      setDocumentError(`${documentType} inválido. Por favor, verifique os dados informados.`);
      setShowAdditionalFields(false);
    } else {
      setDocumentError("");
      setShowAdditionalFields(true);
    }
  };

  console.log('Mensagem debug empresa:', cpf ? "CPF" : "CNPJ");

  // Correção final da função handleSubmit para pessoa física

  const handleSubmit = async (formData, { setSubmitting }) => {
    setFormSubmitAttempted(true);
    setSubmitting(true);
    try {
      await logout();
    } catch { }
    try {
      if (!dataUsageAgreementChecked) {
        Swal.fire({
          icon: "error",
          title: "Atenção",
          text: "É necessário aceitar os Termos de Uso.",
          confirmButtonColor: "#CB3D42",
        });
        setSubmitting(false);
        return;
      }

      const documentClean = formData.document
        ? formData.document
          .replaceAll(".", "")
          .replaceAll("-", "")
          .replaceAll("/", "")
        : "";

      // Verificar se o documento tem o número correto de dígitos
      const isCpf = documentClean.length <= 11;
      if ((isCpf && documentClean.length !== 11) ||
        (!isCpf && documentClean.length !== 14)) {
        Swal.fire({
          icon: "error",
          title: "Atenção",
          text: `${isCpf ? "CPF" : "CNPJ"} incompleto. Por favor, insira todos os dígitos.`,
          confirmButtonColor: "#CB3D42",
        });
        setSubmitting(false);
        return;
      }

      // Validar o documento
      const isValidDocument = isCpf ?
        validateCPF(documentClean) :
        validateCNPJ(documentClean);

      if (!isValidDocument) {
        Swal.fire({
          icon: "error",
          title: "Documento inválido",
          text: `O ${isCpf ? "CPF" : "CNPJ"} informado não é válido. Por favor, verifique os dados e tente novamente.`,
          confirmButtonColor: "#CB3D42",
        });
        setSubmitting(false);
        return;
      }

      const whoIs = isCpf ? "birthdate" : "date_foundation";

      // Verifica se data existe e tem formato correto
      if (!formData[whoIs] || formData[whoIs].length < 10) {
        Swal.fire({
          icon: "error",
          title: "Atenção",
          text: `${isCpf ? "Data de nascimento" : "Data de fundação"} inválida`,
          confirmButtonColor: "#CB3D42",
        });
        setSubmitting(false);
        return;
      }

      const day = formData[whoIs].slice(0, 2);
      const month = formData[whoIs].slice(3, 5);
      const year = formData[whoIs].slice(6, 10);

      const birth = `${year}-${month}-${day}`;

      // Dados base comuns para ambos os tipos
      const requestData = {
        is_company: !isCpf,
        email: formData.email,
        document: documentClean,
        password: formData.password,
        phone: formData.telephone,
        data_usage_agreement: dataUsageAgreementChecked
      };

      // Configurar os campos person e company corretamente:
      if (isCpf) {
        // Para CPF (pessoa física) - person deve ser um objeto direto, não um array
        requestData.person = {
          name: formData.name || '',
          surname: formData.surname || '',
          gender: formData.gender || '',
          gender_detail: formData.gender === 3 ? formData.gender_detail : null,
          date_birth: birth,
        };
        // Importante: não incluir company para CPF
      } else {
        // Para CNPJ (pessoa jurídica)
        const cleanedDocumentState = formData.document_state
          ? formData.document_state.replace(/\D/g, '')
          : '';

        requestData.company = {
          name: formData.name || '',
          fantasy_name: formData.fantasy_name || '',
          date_foundation: birth,
          document_state: cleanedDocumentState,
          state_registration: cleanedDocumentState
        };
        // Importante: não incluir person para CNPJ
      }

      console.log('Dados sendo enviados:', JSON.stringify(requestData));

      try {
        // Adicionar logs para depuração
        console.log('Tipo de pessoa:', isCpf ? 'Física (CPF)' : 'Jurídica (CNPJ)');
        console.log('Estrutura do person:', isCpf ? 'objeto direto' : 'não incluído');
        console.log('Estrutura do company:', !isCpf ? 'objeto' : 'não incluído');
        const responseData = await createUser(requestData);
        clearEmailsValidated();
        clearCpfsValidated();
        if (responseData.data) {
          handleSuccess(requestData.email, requestData.password);
        } else {
          handleError(responseData);
        }
      } catch (apiErr) {
        console.error('API error:', apiErr);
        handleError(apiErr);
      }
    } catch (err) {
      console.log('Erro completo:', err);
      handleError(err);
      setError(m("internalError"));
    }
    setSubmitting(false);
  };
  // Corrigir problema de formatação ao redirecionar da tela de login
  useEffect(() => {
    // Se temos um documento no locationState
    if (locationState.document) {
      // Limpa a formatação primeiro para ter apenas os dígitos
      const digits = locationState.document.replace(/\D/g, '');

      // Determina se é CPF ou CNPJ
      const isCpf = digits.length <= 11;
      setCpf(isCpf);
      setExpectedDigits(isCpf ? 11 : 14);
      setDocumentType(isCpf ? "CPF" : "CNPJ");

      // Aplica a formatação adequada e atualiza o campo
      const formattedValue = formatDocument(digits);

      // Atualiza o campo do formulário com o valor formatado
      if (ref.current) {
        setTimeout(() => {
          ref.current.setFieldValue('document', formattedValue);

          // Validar o documento sem mostrar erros ainda
          const isValidDocument = isCpf ?
            (digits.length === 11 && validateCPF(digits)) :
            (digits.length === 14 && validateCNPJ(digits));

          setDocumentIsValid(isValidDocument);
          setDocumentDigitsCount(digits.length);

          if (isValidDocument) {
            setShowAdditionalFields(true);
            setDocumentError("");
          } else {
            // Não mostrar campos adicionais para documento inválido
            // mas também não mostrar erro inicialmente
            setShowAdditionalFields(false);
            setDocumentError("");
          }
        }, 100);
      }
    } else {
      // Comportamento padrão se não houver documento
      setCpf(true);
      setExpectedDigits(11);
      setDocumentType("CPF");
    }
  }, [locationState.document]);

  const handlePasswordChange = (e) => {
    const value = ref?.current?.values?.password || '';

    setPasswordCriteria({
      uppercase: /[A-Z]/.test(value),
      lowercase: /[a-z]/.test(value),
      number: /[0-9]/.test(value),
      length: value.length >= 8,
    });
  };

  // Verificar campos inválidos para exibir mensagem de ajuda
  // Só exibir mensagens de erro após a primeira tentativa de submissão
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);

  useEffect(() => {
    if (formSubmitAttempted && ref.current && ref.current.errors) {
      const errors = ref.current.errors;
      const invalidFields = Object.keys(errors);
      setInvalidFieldsList(invalidFields);
    } else if (!formSubmitAttempted) {
      setInvalidFieldsList([]);
    }
  }, [ref.current?.errors, formSubmitAttempted]);

  return (
    <>
      <ButtonBack onClick={() => history.goBack()} />
      <Container>
        <TabHeader>
          <Text variant="title1">Minha Conta</Text>
          <Text variant="regular">Cadastro</Text>
        </TabHeader>

        <ErrorBox error={error} />

        <Formik
          initialValues={{ ...INITIAL_FORM_VALUES, ...locationState }}
          validationSchema={cpf ? registerSchemaCpf : registerSchemaCnpj}
          validateOnMount
          validateOnChange={true}
          onSubmit={handleSubmit}
          innerRef={ref}
        >
          {({ isSubmitting, isValid, isValidating, values, setFieldValue, errors, touched }) => (
            <Form className="form login__form">
              {isValidating && <Loading text="Validando" />}
              <FormGroup>
                <Input
                  name="email"
                  type="text"
                  label="E-mail"
                  autoFocus
                  maxLength={100}
                />
                <ErrorMessage name="email" />
              </FormGroup>
              <Spacing size={30} />
              <FormGroup>
                <Input
                  name="document"
                  type="text"
                  label="CPF ou CNPJ"
                  onChange={handleDocumentChange}
                  value={values.document}
                />
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "4px"
                }}>
                  <small style={{
                    fontSize: "12px",
                    color: documentDigitsCount === expectedDigits ? "#28a745" : "#6c757d"
                  }}>
                    Tipo: {documentType} {documentIsValid && "✓"}
                  </small>
                  <small style={{
                    fontSize: "12px",
                    color: documentDigitsCount === expectedDigits ? "#28a745" : "#6c757d"
                  }}>
                    {documentDigitsCount}/{expectedDigits} dígitos
                  </small>
                </div>
                {documentError && (
                  <small style={{
                    fontSize: "12px",
                    color: "#dc3545",
                    display: "block",
                    marginTop: "4px"
                  }}>
                    {documentError}
                  </small>
                )}
                <ErrorMessage name="document" />
              </FormGroup>

              {showAdditionalFields && (
                <>
                  <Spacing size={30} />
                  {cpf ? (
                    <>
                      <FormGroupLine>
                        <FormGroupColumn>
                          <Input
                            name="name"
                            type="text"
                            label="Nome"
                            maxLength={50}
                            onChange={(event) => {
                              const value = removeNumbers(event.target.value);
                              setFieldValue('name', value);
                            }}
                            value={values.name}
                          />
                          <ErrorMessageWrapper>
                            <ErrorMessage name="name" />
                          </ErrorMessageWrapper>
                        </FormGroupColumn>
                        <FormGroupColumn>
                          <Input
                            name="surname"
                            type="text"
                            label="Sobrenome"
                            maxLength={100}
                            onChange={(event) => {
                              const value = removeNumbers(event.target.value);
                              setFieldValue('surname', value);
                            }}
                            value={values.surname}
                          />
                          <ErrorMessageWrapper>
                            <ErrorMessage name="surname" />
                          </ErrorMessageWrapper>
                        </FormGroupColumn>
                      </FormGroupLine>
                    </>
                  ) : (
                    <>
                      <FormGroup>
                        <Input
                          name="name"
                          type="text"
                          label="Razão Social"
                          maxLength={150}
                        />
                        <ErrorMessage name="name" />
                      </FormGroup>
                      <Spacing size={30} />
                      <FormGroup>
                        <Input
                          name="fantasy_name"
                          type="text"
                          label="Nome Fantasia"
                          maxLength={100}
                        />
                        <ErrorMessage name="fantasy_name" />
                      </FormGroup>
                    </>
                  )}
                  <Spacing size={30} />
                  {cpf ? (
                    <FormGroup>
                      <Input
                        name="birthdate"
                        type="text"
                        mask="DD/DD/DDDD"
                        label="Nascimento"
                      />
                      <ErrorMessage name="birthdate" />
                    </FormGroup>
                  ) : (
                    <FormGroup>
                      <Input
                        name="date_foundation"
                        type="text"
                        mask="DD/DD/DDDD"
                        label="Data de Fundação"
                      />
                      <ErrorMessage name="date_foundation" />
                    </FormGroup>
                  )}
                  <Spacing size={30} />
                  <FormGroup>
                    <Input
                      name="telephone"
                      type="text"
                      mask={["(DD)DDDD-DDDD", "(DD)DDDDD-DDDD"]}
                      label={cpf ? "Celular" : "Telefone"}
                      maxLength={15}
                    />
                    <ErrorMessage name="telephone" />
                  </FormGroup>
                  <Spacing size={30} />
                  {cpf ? (
                    <FormGroup>
                      <Label>Gênero</Label>
                      <RadioGroup
                        name="gender"
                        options={[
                          { value: 2, label: "Feminino" },
                          { value: 1, label: "Masculino" },
                          { value: 3, label: "Outros" },
                          { value: 4, label: "Prefiro não responder" },
                        ]}
                      />
                      <ErrorMessage name="gender" />
                    </FormGroup>
                  ) : (
                    <FormGroup>
                      <Input
                        name="document_state"
                        type="text"
                        label="Inscrição estadual"
                        value={values.document_state}
                        onChange={(e) => {
                          const formattedValue = formatNumbers(e.target.value);
                          setFieldValue('document_state', formattedValue);
                        }}
                      />
                    </FormGroup>
                  )}
                  <Spacing size={30} />
                  <FormGroup>
                    <Input
                      name="password"
                      type="password"
                      label="Crie uma senha"
                      onKeyUp={handlePasswordChange}
                      maxLength={50}
                    />
                    <ErrorMessage name="password" />
                  </FormGroup>
                  <Spacing size={30} />
                  <FormGroup>
                    <Input
                      name="password_confirmation"
                      type="password"
                      label="Confirme sua senha"
                      maxLength={50}
                    />
                    <ErrorMessage name="password_confirmation" />
                  </FormGroup>
                  <Spacing size={40} />
                  <PasswordRules rules={passwordCriteria} />
                  <FormGroup>
                    <Label>
                      <input
                        type="checkbox"
                        name="dataUsageAgreement"
                        required
                        style={{ marginRight: "5px", marginBottom: "10px" }}
                        checked={dataUsageAgreementChecked}
                        onChange={handleCheckboxChange}
                      />
                      Declaro que concordo e estou ciente das utilizações dos meus{" "}
                      <span
                        style={{
                          color: "#7f7e7e",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() => setModalOpen(true)}
                      >
                        Dados Pessoais
                      </span>
                    </Label>
                    <ErrorMessage name="dataUsageAgreement" />
                  </FormGroup>

                  {/* Modal */}
                  {modalOpen && (
                    <SimpleModal onClose={() => setModalOpen(false)}>
                      <p style={{ padding: "15px" }}>
                        As informações são livremente informadas pelo usuário, para
                        garantir a sua própria segurança, autorizando expressamente
                        o Zona Sul, a realizar todas as diligências que este julgue
                        necessárias para verificar a exatidão e a veracidade das
                        informações fornecidas, restando sujeito às pertinentes
                        penalidades legais caso sua veracidade não seja, de qualquer
                        modo, confirmada.
                        <br></br>
                        <br></br>Nós do Zona Sul valorizamos a privacidade e
                        proteção dos seus dados pessoais. Seus dados coletados neste
                        cadastro serão utilizados para única e exclusiva finalidade
                        de realizar o cadastro que habilita o uso da plataforma e
                        seus serviços.
                        <br></br>
                        <br></br>
                        Seus dados serão compartilhados com nossa parceira VTEX,
                        responsável pela base de dados, e não compartilharemos com
                        nenhum outro operador ou controlador, salvaguarda, para o
                        cumprimento de obrigações dispostas por lei, pelo poder
                        judiciário e/ou por qualquer outra autoridade competente.
                        <br></br>
                        <br></br>As informações permanecerão armazenadas pelo
                        período de 05 anos após a inatividade do seu cadastro,
                        seguido do devido descarte.
                      </p>
                    </SimpleModal>
                  )}

                  <Button
                    variant="buttonPrimary"
                    text="Cadastrar"
                    type="submit"
                    disabled={!isValid}
                    loading={isSubmitting}
                    style={{ marginTop: "25px" }}
                  />

                  {!isValid && formSubmitAttempted && invalidFieldsList.length > 0 && (
                    <div style={{
                      marginTop: "10px",
                      padding: "10px",
                      backgroundColor: "#f8d7da",
                      borderRadius: "4px",
                      color: "#721c24",
                      fontSize: "14px"
                    }}>
                      <p><strong>Por favor, corrija os seguintes campos para continuar:</strong></p>
                      <ul style={{ marginLeft: "20px", marginTop: "5px" }}>
                        {invalidFieldsList.map((field) => (
                          <li key={field}>
                            {field === "email" && "E-mail"}
                            {field === "document" && documentType}
                            {field === "name" && (cpf ? "Nome" : "Razão Social")}
                            {field === "surname" && "Sobrenome"}
                            {field === "fantasy_name" && "Nome Fantasia"}
                            {field === "birthdate" && "Data de Nascimento"}
                            {field === "date_foundation" && "Data de Fundação"}
                            {field === "telephone" && (cpf ? "Celular" : "Telefone")}
                            {field === "gender" && "Gênero"}
                            {field === "password" && "Senha"}
                            {field === "password_confirmation" && "Confirmação de Senha"}
                            {field === "dataUsageAgreement" && "Aceite dos Termos de Uso"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </Form>
          )}
        </Formik>
        <Text variant="support">
          Ao se cadastrar, você concorda com nossa{" "}
          <a
            target="_blank"
            href="https://www.zonasul.com.br/politica-de-privacidade"
            rel="noopener noreferrer"
          >
            <span className="semibold">Política de Privacidade</span>
          </a>{" "}
          ou, se preferir, entre em contato com nosso{" "}
          <a
            target="_blank"
            href="https://www.zonasul.com.br/ajuda/fale-com-o-dpo"
            rel="noopener noreferrer"
          >
            <span className="semibold">Encarregado de Dados</span>
          </a>{" "}
          pelo canal oficial disponibilizado em nosso site.
        </Text>
      </Container>
    </>
  );
};

export default Register;
